// US-074 — Auto-mode draft preview + sending bubble.
//
// When `aiMode === 'auto'` and an AI draft exists, the thread shows the
// SendingBubble until delivery is confirmed (`sent === true`), then a sent
// bubble + `✓ Envoyé`. Mirrors `ls-inbox.jsx:254-269` (sending) and
// `:745-756` (sent).
//
// Adversarial-review fix (2026-07-21) — `ThreadPanel` no longer drives this
// from an optimistic client-side send (HTTP 201 = queued, not delivered).
// For pure-auto accounts (no hot-override) it now only ever passes
// `sent={false}` — a real send never happens client-side there, and the
// component is unmounted entirely (not flipped to `sent`) once the
// delivery-truth bubble (`MessageBubbles`, `deliveryState`) supersedes it,
// so the `sent === true` branch below is effectively dead in that call site
// today. It's kept + tested because it's still a faithful "confirmed
// delivered" rendering for any future caller that has real confirmed truth
// to pass (e.g. driving it straight off a `deliveryState === 'sent'` row).
//
// Rendered as a direct child of an `<ol>`, hence the outer `<li>`. We don't
// compose the DS `MessageBubble` (which would nest a second `<li>`) — we
// reuse the `data-ls-component="message-bubble"` styling primitives by
// rendering a styled `<span>` here.

import { useT } from '@barel/i18n';

export interface AutoPreviewProps {
  body: string;
  /** True once the extension confirms delivery (`message.out` event). */
  sent: boolean;
}

const SENDING_BUBBLE: React.CSSProperties = {
  display: 'inline-block',
  background: 'var(--bg-elevated)',
  color: 'var(--text-secondary)',
  borderRadius: 'var(--radius-bubble-out)',
  padding: 'var(--space-2_5) var(--space-3)',
  fontSize: 'var(--text-sm)',
  lineHeight: 'var(--line-sm)',
  fontFamily: 'var(--font-sans)',
  maxWidth: '78%',
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
  opacity: 0.75,
};

const SENT_BUBBLE: React.CSSProperties = {
  ...SENDING_BUBBLE,
  color: 'var(--text-primary)',
  opacity: 1,
};

export function AutoPreview({ body, sent }: AutoPreviewProps) {
  const t = useT('inbox');
  if (!body) return null;
  if (!sent) {
    return (
      <li
        data-ls-component="auto-preview"
        data-state="sending"
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-end',
          gap: 'var(--space-1)',
          listStyle: 'none',
        }}
      >
        <span data-ls-component="message-bubble" data-role="sending" style={SENDING_BUBBLE}>
          {body}
        </span>
        <span
          style={{
            fontSize: 'var(--text-2xs)',
            lineHeight: 'var(--line-xs)',
            color: 'var(--accent)',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 'var(--space-1)',
          }}
        >
          <ClockIcon />
          {t('thread.draft.sending')}
        </span>
      </li>
    );
  }
  return (
    <li
      data-ls-component="auto-preview"
      data-state="sent"
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
        gap: 'var(--space-1)',
        listStyle: 'none',
      }}
    >
      <span data-ls-component="message-bubble" data-role="out" style={SENT_BUBBLE}>
        {body}
      </span>
      <span
        style={{
          fontSize: 'var(--text-2xs)',
          lineHeight: 'var(--line-xs)',
          color: 'var(--success)',
        }}
      >
        {t('thread.draft.sent')}
      </span>
    </li>
  );
}

function ClockIcon() {
  return (
    <svg
      width={11}
      height={11}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2.2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <circle cx={12} cy={12} r={10} />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  );
}
